import React, { useState, useCallback, useMemo, useEffect } from 'react';
// @mui
import {
  Container,
  Stack,
  Button
} from '@mui/material';
// @iconify
import { Icon } from '@iconify/react';
// components
import Page from '../../components/Page';
import {
  TokenList,
  TokenForm,
  TokenView,
  ConfirmDialog
} from '../../components/ocpi/tokens';
// hooks
import { useUpdateToken, usePatchToken, useDeleteToken } from 'src/hooks/useOcpiTokens.hook';
import { useFetchOnDemand } from 'src/hooks/useFetch.hook';
import { useNavigate } from 'react-router-dom';

export default function OCPITokens() {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [selectedTokenIds, setSelectedTokenIds] = useState([]);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 50,
    total: 0
  });

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // Form states
  const [editingToken, setEditingToken] = useState(null);
  const [viewingToken, setViewingToken] = useState(null);
  
  // Confirmation states
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  // Navigation
  const navigate = useNavigate();

  // API hooks
  const { data: tokensData, isLoading, error, mutate: fetchTokensList } = useFetchOnDemand("partnerGetAllTokens", false, false);
  const updateTokenMutation = useUpdateToken();
  const patchTokenMutation = usePatchToken();
  const deleteTokenMutation = useDeleteToken();

  // Data mapping function to convert OCPI token data to form format
  const mapTokenToFormData = useCallback((token) => {
    if (!token) return null;
    
    return {
      countryCode: token.country_code || token.partner_country_code || '',
      partyId: token.party_id || token.partner_party_id || '',
      uid: token.uid || token.token_uid || '',
      type: token.type || token.token_type || 'RFID',
      whitelist: token.whitelist || 'Allowed',
      contractId: token.contract_id || '',
      issuer: token.issuer || token.partner_name || '',
      defaultProfileType: token.default_profile_type || 'REGULAR',
      visualNumber: token.visual_number || '',
      groupId: token.group_id || '',
      language: token.language || 'en',
      energyContract: token.energy_contract || {
        supplierName: '',
        contractId: ''
      },
      valid: token.valid !== undefined ? token.valid : true
    };
  }, []);

  // Extract tokens from API response
  const tokens = useMemo(() => {
    if (tokensData?.data) {
      return tokensData.data;
    }
    return [];
  }, [tokensData]);

  // Update pagination from API response
  useEffect(() => {
    if (tokensData?.headers) {
      const totalCount = tokensData.headers['x-total-count'] || tokens.length;
      setPagination(prev => ({
        ...prev,
        total: totalCount
      }));
    }
  }, [tokensData, tokens.length]);

  const getTokensList = useCallback(() => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
      search: searchTerm,
      type: typeFilter,
      status: statusFilter
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    if (typeFilter && typeFilter !== "All Types") {
      params.type = typeFilter;
    }

    if (statusFilter && statusFilter !== "All Status") {
      params.status = statusFilter;
    }

    fetchTokensList(params);
  }, [pagination.offset, pagination.limit, searchTerm, typeFilter, statusFilter]);

  useEffect(() => {
    getTokensList();
  }, [pagination.offset, pagination.limit, searchTerm, typeFilter, statusFilter]);

  // Handler functions
  const handleCreateToken = () => {
    setEditingToken(null);
    setFormOpen(true);
  };

  const handleEditToken = (tokenId) => {
    const token = tokensData.find(t => (t.id || t.uid) === tokenId);
    const mappedToken = mapTokenToFormData(token);
    setEditingToken(mappedToken);
    setFormOpen(true);
  };

  const handleViewToken = (tokenId) => {
    const token = tokensData.find(t => (t.id || t.uid) === tokenId);
    setViewingToken(token);
    setViewOpen(true);
  };

  const handleDeleteToken = (tokenId) => {
    setConfirmAction('delete');
    setConfirmData({ tokenId });
    setConfirmOpen(true);
  };

  const handleFormClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    setFormOpen(false);
    setEditingToken(null);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewingToken(null);
  };

  const handleViewEdit = () => {
    const mappedToken = mapTokenToFormData(viewingToken);
    setViewingToken(null);
    setViewOpen(false);
    setEditingToken(mappedToken);
    setFormOpen(true);
  };

  const handleFormSubmit = async (tokenData, s) => {
    try {
      if (editingToken) {
        // Update existing token
        await patchTokenMutation.mutateAsync(tokenData);
      } else {
        // Create new token
       await updateTokenMutation.mutateAsync(tokenData);
      }
      setFormOpen(false);
      setEditingToken(null);
    } catch (error) {
      console.error('Error submitting token form:', error);
    } finally {
      s(!1);
    }
  };

  const handleConfirm = async () => {
    try {
      switch (confirmAction) {
        case 'delete':
          const token = tokens.find(t => (t.id || t.uid) === confirmData.tokenId);
          if (token) {
            const { country_code, party_id, uid } = token;
            await deleteTokenMutation.mutateAsync({
              countryCode: country_code,
              partyId: party_id,
              uid
            });
          }
          break;
        case 'deleteMultiple':
          // Delete multiple tokens
          for (const tokenId of confirmData.tokenIds) {
            const token = tokens.find(t => (t.id || t.uid) === tokenId);
            if (token) {
              const { country_code, party_id, uid } = token;
              await deleteTokenMutation.mutateAsync({
                countryCode: country_code,
                partyId: party_id,
                uid
              });
            }
          }
          break;
        case 'activate':
          // Activate multiple tokens
          for (const tokenId of confirmData.tokenIds) {
            const token = tokens.find(t => (t.id || t.uid) === tokenId);
            if (token) {
              const { country_code, party_id, uid } = token;
              await patchTokenMutation.mutateAsync({
                countryCode: country_code,
                partyId: party_id,
                uid,
                tokenData: { valid: true }
              });
            }
          }
          break;
        case 'deactivate':
          // Deactivate multiple tokens
          for (const tokenId of confirmData.tokenIds) {
            const token = tokens.find(t => (t.id || t.uid) === tokenId);
            if (token) {
              const { country_code, party_id, uid } = token;
              await patchTokenMutation.mutateAsync({
                countryCode: country_code,
                partyId: party_id,
                uid,
                tokenData: { valid: false }
              });
            }
          }
          break;
        default:
          break;
      }
      setConfirmOpen(false);
      setConfirmAction(null);
      setConfirmData(null);
    } catch (error) {
      console.error('Error in confirmation action:', error);
    }
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleActivateSelected = () => {
    if (selectedTokenIds.length === 0) return;
    setConfirmAction('activate');
    setConfirmData({ tokenIds: selectedTokenIds });
    setConfirmOpen(true);
  };

  const handleDeactivateSelected = () => {
    if (selectedTokenIds.length === 0) return;
    setConfirmAction('deactivate');
    setConfirmData({ tokenIds: selectedTokenIds });
    setConfirmOpen(true);
  };

  const handleDeleteSelected = () => {
    if (selectedTokenIds.length === 0) return;
    setConfirmAction('deleteMultiple');
    setConfirmData({ tokenIds: selectedTokenIds });
    setConfirmOpen(true);
  };

  const handleTokenSelect = useCallback((tokenId) => {
    setSelectedTokenIds(prev =>
      prev.includes(tokenId)
        ? prev.filter(id => id !== tokenId)
        : [...prev, tokenId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTokenIds.length === tokensData.length) {
      setSelectedTokenIds([]);
    } else {
      setSelectedTokenIds(tokensData.map(token => token.id || token.uid));
    }
  }, [selectedTokenIds.length, tokensData]);

  const handleClearSelection = useCallback(() => {
    setSelectedTokenIds([]);
  }, []);

  const handlePageChange = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      offset: page * prev.limit
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPagination(prev => ({
      ...prev,
      limit: pageSize,
      offset: 0
    }));
  }, []);

  const handleRefresh = useCallback(() => {
    getTokensList();
  }, [getTokensList]);

  const handleExportAll = useCallback(() => {
    try {
      const dataStr = JSON.stringify(tokens, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tokens_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export tokens:', err);
    }
  }, [tokens]);

  const handleExportSelected = useCallback(() => {
    try {
      const selectedTokens = tokens.filter(token => selectedTokenIds.includes(token.id || token.uid));
      const dataStr = JSON.stringify(selectedTokens, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `selected_tokens_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export selected tokens:', err);
    }
  }, [tokens, selectedTokenIds]);

  const handleImport = useCallback((importedData) => {
    try {
      if (Array.isArray(importedData)) {
        // Import tokens one by one
        importedData.forEach(tokenData => {
          updateTokenMutation.mutate(tokenData);
        });
      }
    } catch (error) {
      console.error('Failed to import tokens:', error);
    }
  }, [updateTokenMutation]);

  const getConfirmDialogProps = () => {
    switch (confirmAction) {
      case 'delete':
        return {
          title: 'Delete Token',
          content: 'Are you sure you want to delete this token? This action cannot be undone.',
          confirmText: 'Delete',
          confirmColor: 'error'
        };
      case 'deleteMultiple':
        return {
          title: 'Delete Multiple Tokens',
          content: `Are you sure you want to delete ${confirmData?.tokenIds?.length || 0} selected tokens? This action cannot be undone.`,
          confirmText: 'Delete All',
          confirmColor: 'error'
        };
      case 'activate':
        return {
          title: 'Activate Tokens',
          content: `Are you sure you want to activate ${confirmData?.tokenIds?.length || 0} selected tokens?`,
          confirmText: 'Activate',
          confirmColor: 'success'
        };
      case 'deactivate':
        return {
          title: 'Deactivate Tokens',
          content: `Are you sure you want to deactivate ${confirmData?.tokenIds?.length || 0} selected tokens?`,
          confirmText: 'Deactivate',
          confirmColor: 'warning'
        };
      default:
        return {
          title: 'Confirm Action',
          content: 'Are you sure you want to proceed?',
          confirmText: 'Confirm',
          confirmColor: 'primary'
        };
    }
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  return (
    <Page title="OCPI Tokens">
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={handleBackClick}
              startIcon={<Icon icon="eva:arrow-back-fill" width={20} height={20} />}
            >
              Back to Dashboard
            </Button>
          </Stack>

          {/* Token List */}
          <TokenList
            tokens={tokensData}
            loading={isLoading}
            error={error?.message}
            selectedTokenIds={selectedTokenIds}
            searchTerm={searchTerm}
            typeFilter={typeFilter}
            statusFilter={statusFilter}
            pagination={pagination}
            onSearchChange={setSearchTerm}
            onTypeFilterChange={setTypeFilter}
            onStatusFilterChange={setStatusFilter}
            onTokenSelect={handleTokenSelect}
            onTokenView={handleViewToken}
            onTokenEdit={handleEditToken}
            onTokenDelete={handleDeleteToken}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onActivateSelected={handleActivateSelected}
            onDeactivateSelected={handleDeactivateSelected}
            onDeleteSelected={handleDeleteSelected}
            onExportAll={handleExportAll}
            onExportSelected={handleExportSelected}
            onImport={handleImport}
            onCreateToken={handleCreateToken}
            onRefresh={handleRefresh}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          {/* Token Form Dialog */}
          <TokenForm
            open={formOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            initialValues={editingToken}
            isEdit={!!editingToken}
          />

          {/* Token View Dialog */}
          <TokenView
            open={viewOpen}
            onClose={handleViewClose}
            onEdit={handleViewEdit}
            token={viewingToken}
          />

          {/* Confirmation Dialog */}
          <ConfirmDialog
            open={confirmOpen}
            onClose={handleConfirmClose}
            onConfirm={handleConfirm}
            {...getConfirmDialogProps()}
          />
        </Stack>
      </Container>
    </Page>
  );
}
